import { findParentNodeClosestToPos, type Range } from '@tiptap/core';
import type { ResolvedPos } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';

/** A paragraph inside a task item inside a task list, so a task item's own text is never shallower. */
export const TASK_ITEM_TEXT_DEPTH = 3;

/**
 * Resolves the range covering the text of the task item containing the selection.
 *
 * Only the item's own paragraph is covered. Nested sub-tasks live in a trailing `taskList`
 * and are separate tasks, and the checkbox/chips/actions are node chrome outside the
 * editable content. A selection inside a sub-task resolves that sub-task's own text.
 *
 * Returns `undefined` when the selection isn't inside a task item, or when the item is empty.
 */
export const getTaskItemTextRange = (state: EditorState): Range | undefined => {
    const taskItem = findParentNodeClosestToPos(
        state.selection.$from,
        (node) => node.type.name === TiptapNodeType.taskItem,
    );

    if (!taskItem) return undefined;

    const textNode = taskItem.node.firstChild;

    if (!textNode?.content.size) return undefined;

    // `taskItem.start` is the position of its first child (the text paragraph), so that
    // paragraph's content starts one position further in, past its own opening token.
    const from = taskItem.start + 1;

    return { from, to: from + textNode.content.size };
};

/**
 * Whether the selection covers the whole of one task item's own text, and nothing else.
 *
 * Deliberately strict: a partial selection is ordinary text, and only a selection taking an item's
 * text from end to end reads as "this whole task".
 */
export const isWholeTaskItemTextSelection = ($from: ResolvedPos, $to: ResolvedPos): boolean => {
    // An empty item's caret sits at offset 0 of an empty paragraph, which would otherwise satisfy
    // every check below. Neither caller acts on an empty selection, so this only keeps the
    // predicate true to its name rather than relying on that.
    if ($from.pos === $to.pos) return false;
    if (!$from.sameParent($to)) return false;
    if ($from.depth < TASK_ITEM_TEXT_DEPTH) return false;
    if ($from.parent.type.name !== TiptapNodeType.paragraph) return false;

    // The item's own text is its first child, so a caret in a sub-task resolves that sub-task.
    if ($from.index($from.depth - 1) !== 0) return false;
    if ($from.node($from.depth - 1).type.name !== TiptapNodeType.taskItem) return false;
    if ($from.node($from.depth - 2).type.name !== TiptapNodeType.taskList) return false;

    return $from.parentOffset === 0 && $to.parentOffset === $to.parent.content.size;
};
